/**
 * 对话页（T06）。
 *
 * 主链路：输入 → `createProviderAdapter()` → `for await` 消费 `StreamChunk`
 * → 增量渲染 → 落库（`messageRepo`）。
 *
 * 关键契约（对齐 docs/CODE-TASKS.md 第 5 节）：
 * - 助手消息 status 走 `pending → streaming → done/error`；用户消息落库即 `done`；
 * - 取消走 `AbortSignal`：适配器静默结束，UI 用 `signal.aborted` 判定并保留已收到内容；
 * - 没有可用 provider / 没有模型时给出空状态引导，不抛异常、不白屏；
 * - 每个可见部件都通过 `useComponentMeta()` 登记 390x844 逻辑坐标；
 * - 动效一律引用 `src/design/motion.ts` 的冻结预设（tap / listStagger / sheetBackdrop）。
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, CSSProperties, KeyboardEvent, ReactElement } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { liveQuery } from 'dexie';
import { apiClient } from '../../core/api/client';
import { newId } from '../../core/ids';
import { filterGeneratedChunks } from '../../core/content/filterGeneratedLinks';
import { acknowledgeChatJob, activeChatController, backgroundChatEnabled, createBackgroundChatAdapter, registerChatRun } from './backgroundChat';

import { navigate } from '../../app/hashRoute';
import type { GeneratedImage, ResolvedImageConfig } from '../../core/image';
import { SERVER_PROVIDER_ID, providerPurpose } from '../../core/providers';
import type { ProviderAdapter, ResolvedProviderConfig } from '../../core/providers';
import {
  conversationRepo,
  memoryRepo,
  messageRepo,
  presetRepo,
  providerRepo,
} from '../../core/repositories';
import { loadModelSettings, updateModelSettings } from '../../core/settings';
import { resolveRoleConversation, validateRoleModelLimits } from '../../core/roleModel';
import { applyRoleContextBudget } from '../../core/roleContext';
import { buildIntimacyContext } from '../intimacy/client';
import type {
  Attachment,
  ChatMessage,
  Conversation,
  MemoryEntry,
  Preset,
  ProviderConfig,
} from '../../core/types';
import { useMotionPresets } from '../../design/motion';
import type { MotionPresets } from '../../design/motion';
import { AppIcon, buildComponentMeta, useComponentMeta } from '../../ui';
import type { ComponentMetaInput } from '../../ui';
import { AttachmentStrip } from '../attachments/AttachmentStrip';
import { persistAttachments } from '../attachments/persist';
import { readSecret } from '../providers/secretStore';
import { pickAttachments } from '../attachments/select';
import type { PickResult } from '../attachments/select';
import { saveGeneratedImages } from '../gallery/save';
import { roleGalleryHash } from '../gallery/galleryScope';
import type { SaveGeneratedImagesOptions, SaveGeneratedImagesResult } from '../gallery/save';
import {
  AssistantTtsButton,
  VoiceComposerControls,
  VoiceProvider,
  createDefaultVoiceDeps,
  ttsButtonLayout,
  useVoiceController,
} from '../voice';
import type { VoiceDeps } from '../voice';
import { mimoClient, TtsModeSelector } from '../mimo';
import { ChatControlsDrawer } from './ChatControlsDrawer';
import { clearDraft, readDraft, writeDraft } from './draftStore';
import { AssistantVoiceBubble, AssistantVoicePending } from './AssistantVoiceBubble';
import { loadVoiceDisplayPreferences, readVoiceReply, rememberVoiceReply } from '../mimo/displayPreferences';
import type { TtsAccount, TtsMode } from '../mimo';
import type { SearchResult } from '../../core/search';
import {
  CitationSources,
  SearchNotice,
  SearchToggle,
  defaultSearchRunner,
  executeSearch,
  loadSearchEnabled,
  saveSearchEnabled,
  searchLayout,
} from '../search';
import type { SearchOutcome, SearchRunner } from '../search';
import { MessageAttachments, MessageImagePreview } from './MessageAttachments';
import { getActiveConversationId, setActiveConversationId } from './activeConversation';
import { RoleSessions } from './RoleSessions';
import {
  defaultImageGenerator,
  providerSupportsImage,
  resolveSelectedImageEntry,
} from './imageEntry';
import { generatedImageAttachments } from './imageEntry';
import type { ImageGenerator } from './imageEntry';
import {
  IMAGE_JOB_FINISHED_EVENT,
  abortImageJob,
  activeImageJobController,
  startImageJob,
} from './imageJobs';
import styles from './ChatPage.module.css';
import {
  COMPOSER_TOP,
  PAGE_TOP,
  chatComponentMeta,
  chatLayout,
  composerAttachmentsLayout,
  messageBubbleLayout,
} from './componentMeta';
import { ContextNotice } from './ContextNotice';
import { PresetSelector } from './PresetSelector';
import { assembleChatMessages } from './contextAssembly';
import { loadActiveProfile } from '../account/profile';
import { useUserAvatar } from '../account/useUserAvatar';
import { useProviderModels } from './useProviderModels';
import type { ContextInjectionSummary } from './contextAssembly';
import { loadOfficialCatalog } from './officialModelCatalog';
import type { OfficialImageOption, OfficialModelOption } from './officialModelCatalog';
import { loadModelPreference, saveModelPreference } from './modelPreference';
import { getFeatureFlags, loadFeatureFlags, useFeatureFlags } from '../../core/featureFlags';
import {
  enqueueAutoMemory,
  loadAutoMemorySettings,
  FOLLOW_CONVERSATION_PROVIDER,
} from '../memory/autoMemory';
import { loadMemoryInjectionMode } from '../memory/injection';
import { getEntitlements } from '../subscription/entitlements';
import {
  applyControlToolCalls,
  automationAllowsBubble,
  automationAllowsMoment,
  buildAutomationInstructions,
  buildRoleTools,
  completedToolCalls,
  mergeToolCallDelta,
  parseRichOutput,
  stripControlMarkers,
} from '../automation/richOutput';
import type { StreamToolCall } from '../automation/richOutput';
import { applyRichOutput, roleImageAvailable, roleVoiceDeliveryActive } from '../automation/media';
import { decideRoleVoice } from '../automation/voiceDecision';
import { modelGateway } from '../modelGateway';
import { loadMessageRoleVoice } from '../mimo/voiceDesign';
import { getMomentAccountId } from '../moments/accountScope';

const NEW_CONVERSATION_TITLE = '新对话';
const CANCELLED_HINT = '已停止生成';
const NO_PROVIDER_HINT = '尚未配置可用的模型提供商';
const SERVER_PROVIDER_CONFIG: ResolvedProviderConfig = {
  id: SERVER_PROVIDER_ID,
  kind: 'custom',
  name: '栀歆模型',
  baseUrl: '',
  apiKeyRef: '',
  enabled: true,
};
function officialModelId(value: string): string {
  const matches = [...value.matchAll(/\(([^()]+)\)/g)];
  return matches[matches.length - 1]?.[1] ?? value;
}

/** Builds a fresh adapter for the user-configured automatic-memory model. */
function resolveAutoMemoryAdapter(
  conversation: Conversation,
  providers: readonly ProviderConfig[],
): { adapter: ProviderAdapter; model: string } | null {
  const settings = loadAutoMemorySettings();
  if (!settings.enabled) return null;
  const providerId = settings.providerId;
  if (providerId === SERVER_PROVIDER_ID) {
    if (settings.model.trim() === '') return null;
    return { adapter: modelGateway.createChatAdapter(SERVER_PROVIDER_CONFIG), model: settings.model.trim() };
  }
  if (providerId === FOLLOW_CONVERSATION_PROVIDER) {
    const model = (conversation.model ?? '').trim();
    if (model === '') return null;
    if (conversation.providerId === SERVER_PROVIDER_ID) {
      return { adapter: modelGateway.createChatAdapter(SERVER_PROVIDER_CONFIG), model: officialModelId(model) };
    }
    const provider = resolveProvider(conversation, providers);
    if (provider === null) return null;
    if (providerPurpose(provider) === 'image' || providerPurpose(provider) === 'tts') return null;
    const apiKey = readSecret(provider.apiKeyRef);
    return {
      adapter: modelGateway.createChatAdapter(apiKey === null ? provider : { ...provider, apiKey }),
      model,
    };
  }
  const provider = providers.find((item) => item.id === providerId && item.enabled);
  if (provider === undefined) return null;
  if (providerPurpose(provider) === 'image' || providerPurpose(provider) === 'tts') return null;
  const model = settings.model.trim() !== '' ? settings.model.trim() : (provider.defaultModel ?? '').trim();
  if (model === '') return null;
  const apiKey = readSecret(provider.apiKeyRef);
  return {
    adapter: modelGateway.createChatAdapter(apiKey === null ? provider : { ...provider, apiKey }),
    model,
  };
}

/** 页面内的绝对定位：清单坐标是画布绝对坐标，DOM 坐标相对 `origin`（默认页面根容器）。 */
/**
 * 输入栏子部件的定位原点。
 *
 * `.composer`（`chat.composer.bar`）自身是 `position: absolute`，因此它会成为内部
 * 绝对定位子元素的**包含块**：子元素的 `top/left` 是相对这条 bar 的左上角算的，
 * 而**不是**相对页面根容器。bar 的页面坐标是 `(0, COMPOSER_TOP)`，所以子元素必须
 * 以 `COMPOSER_TOP` 为原点，才能落到 `componentMeta` 里登记的绝对坐标上。
 *
 * 修复前这里传的是页面原点 `PAGE_TOP`，于是对子元素多叠加的一层 offset（≈700px）
 * 把它们推到了屏幕外 —— 输入框与发送键既看不见也点不到，表现为「只能语音、发不了
 * 文字」。jsdom 没有布局引擎，所以 952 个单测都测不出这类「真实排版」缺陷。
 */
const COMPOSER_CHILD_ORIGIN = { x: 0, y: COMPOSER_TOP } as const;

function boxStyle(
  layout: ComponentMetaInput['layout'],
  zIndex: number,
  origin: { x: number; y: number } = { x: 0, y: PAGE_TOP },
): CSSProperties {
  return {
    position: 'absolute',
    left: layout.x - origin.x,
    top: layout.y - origin.y,
    width: layout.width,
    height: layout.height,
    zIndex,
  };
}

/** 会话标题：取首行、最长 18 个字符。 */
function deriveTitle(text: string): string {
  const firstLine = text.split('\n')[0]?.trim() ?? '';
  const title = firstLine === '' ? NEW_CONVERSATION_TITLE : firstLine;
  return title.length > 18 ? `${title.slice(0, 18)}…` : title;
}

/** 选定本次请求使用的 provider：会话绑定优先，否则用第一个启用的 provider。 */
function resolveProvider(
  conversation: Conversation,
  providers: readonly ProviderConfig[],
): ProviderConfig | null {
  if (conversation.providerId !== undefined) {
    const bound = providers.find((item) => item.id === conversation.providerId);
    if (bound !== undefined && providerPurpose(bound) !== 'tts') return bound;
  }
  return providers.find((provider) => providerPurpose(provider) !== 'tts') ?? null;
}

function formatProviderError(error: { error?: string; code?: string; serverCode?: string; status?: number }): string {
  switch (error.serverCode) {
    case 'unauthorized':
      return '请先登录后再使用官方模型';
    case 'insufficient_balance':
      return '余额不足，请先充值';
    case 'model_unavailable':
      return '官方模型已下架或暂不可用';
    case 'rate_limited':
      return '上游请求过多，请稍后重试';
    case 'service_unavailable':
      return '上游服务暂时不可用，请稍后重试';
    case 'billing_error':
      return '计费失败，请稍后重试';
    default:
      if (error.status === 401) return '请先登录后再使用官方模型';
      if (error.status === 402) return '余额不足，请先充值';
      if (error.status === 429) return '上游请求过多，请稍后重试';
      if (error.status === 503) return '上游服务暂时不可用，请稍后重试';
      return error.error ?? '模型调用失败';
  }
}

interface ReasoningBlockProps {
  baseId: string;
  layout: ComponentMetaInput['layout'];
  reasoning: string;
  presets: MotionPresets;
}

/** 助手消息的思考过程折叠区（默认折叠）。 */
function ReasoningBlock({ baseId, layout, reasoning, presets }: ReasoningBlockProps): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const boxMeta: ComponentMetaInput = {
    id: `${baseId}.reasoning`,
    page: 'chat',
    role: 'container',
    label: '思考过程折叠区',
    layout,
    zIndex: 5,
    props: ['expanded', 'reasoning'],
    notes: '默认折叠，点击标题行展开；安卓用可折叠卡片',
  };
  const boxRef = useComponentMeta<HTMLDivElement>(buildComponentMeta(boxMeta, 'container'));

  return (
    <div
      ref={boxRef}
      className={styles.reasoning}
      data-ui-id={boxMeta.id}
      data-ui-role="container"
      data-expanded={expanded ? 'true' : 'false'}
    >
      <motion.button
        type="button"
        className={styles.reasoningToggle}
        data-ui-id={`${baseId}.reasoningToggle`}
        aria-expanded={expanded}
        aria-label={expanded ? '收起思考过程' : '展开思考过程'}
        whileTap={presets.tap.whileTap}
        transition={presets.tap.transition}
        onClick={() => setExpanded((prev) => !prev)}
      >
        {expanded ? '收起思考过程' : '展开思考过程'}
      </motion.button>
      {expanded ? <p className={styles.reasoningText}>{reasoning}</p> : null}
    </div>
  );
}

interface ErrorBlockProps {
  baseId: string;
  layout: ComponentMetaInput['layout'];
  message: string;
}

/** 助手消息的错误态（status=error 时渲染）。 */
function ErrorBlock({ baseId, layout, message }: ErrorBlockProps): ReactElement {
  const meta: ComponentMetaInput = {
    id: `${baseId}.error`,
    page: 'chat',
    role: 'text',
    label: '消息错误态',
    layout,
    zIndex: 5,
    notes: '错误色文案；安卓用 TextView（errorTextColor）',
  };
  const ref = useComponentMeta<HTMLParagraphElement>(buildComponentMeta(meta, 'text'));
  return (
    <p
      ref={ref}
      className={styles.bubbleError}
      data-ui-id={meta.id}
      data-ui-role="text"
      role="alert"
    >
      {message}
    </p>
  );
}

interface MessageBubbleProps {
  message: ChatMessage;
  rolePreset: Preset | null;
  index: number;
  presets: MotionPresets;
  citations?: SearchResult[];
  /** 点开消息内图片的大图预览（页面层弹层，由 ChatPage 持有）。 */
  onPreview: (attachment: Attachment) => void;
  injection?: ContextInjectionSummary;
  showContextInjection: boolean;
  showReasoning: boolean;
  ttsEnabled?: boolean;
  waitingForAudio?: boolean;
  voiceReply?: { duration: number | null };
  actionsOpen?: boolean;
  onToggleActions?: () => void;
  onDeleteMessage?: () => void;
  onRegenerateMessage?: () => void;
}
/** 只有「确实发生了注入」的摘要才需要渲染信息条；其余（undefined / 空注入）返回 null。 */
function activeInjection(
  summary: ContextInjectionSummary | undefined,
): ContextInjectionSummary | null {
  if (summary === undefined) return null;
  return summary.injected ? summary : null;
}

/** 单条消息气泡：user / assistant / system 三种样式，助手消息带思考折叠区。 */
function MessageBubble({
  message,
  rolePreset,
  index,
  presets,
  citations,
  onPreview,
  injection,
  showContextInjection,
  showReasoning,
  ttsEnabled = true,
  waitingForAudio = false,
  voiceReply,
  actionsOpen = false,
  onToggleActions,
  onDeleteMessage,
  onRegenerateMessage,
}: MessageBubbleProps): ReactElement {
  const bubbleRole: 'user' | 'assistant' = message.role === 'user' ? 'user' : 'assistant';
  const layout = messageBubbleLayout(bubbleRole, index);
  const baseId = `chat.message.${message.role}.${message.id}`;
  const hasAvatar = message.role !== 'system';
  // T23：用户消息的附件 + 生成图片挂在助手消息的 images 上，统一走同一条渲染链路。
  const attachments: Attachment[] = [
    ...(message.attachments ?? []),
    ...generatedImageAttachments(message.id, message.images ?? []),
  ];
  const notice = showContextInjection ? activeInjection(injection) : null;
  const hasInjection = notice !== null;
  const imageGenerating =
    message.imageSending === true || (
      message.imageJob !== undefined && (message.status === 'pending' || message.status === 'streaming')
    );
  const hasPartialReply = message.content.trim() !== '' || attachments.length > 0;
  const pendingRoleVoice = message.roleVoicePending === true && !message.audioError && message.status !== 'error'
    && (Boolean(activeChatController(message.id)) || Boolean(message.backgroundJob && backgroundChatEnabled()));
  waitingForAudio = !message.audioUrl && (waitingForAudio || pendingRoleVoice || (message.audioSending === true && roleVoiceDeliveryActive(message.id)));
  const hasVoice = voiceReply !== undefined || Boolean(message.audioUrl);
  const completed = (message.status === 'done' || (message.status === 'error' && hasPartialReply)) && !imageGenerating && !waitingForAudio;
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  useEffect(() => {
    if (copyState === 'idle') return;
    const timer = window.setTimeout(() => setCopyState('idle'), 2500);
    return () => window.clearTimeout(timer);
  }, [copyState]);
  const copyMessage = async (): Promise<void> => {
    const content = message.content || attachments.map(attachment => attachment.dataUrl ?? attachment.name).join('\n');
    try {
      await navigator.clipboard.writeText(content);
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
  };

  const bubbleMeta: ComponentMetaInput = {
    id: baseId,
    page: 'chat',
    role: 'container',
    label: `${message.role === 'user' ? '用户' : message.role === 'assistant' ? '助手' : '系统'}消息气泡`,
    layout,
    zIndex: 4,
    props: ['content', 'reasoning', 'status'],
    notes: '消息气泡实例；模板坐标见 chat.message.user / chat.message.assistant',
  };
  const textMeta: ComponentMetaInput = {
    id: `${baseId}.text`,
    page: 'chat',
    role: 'text',
    label: '消息正文',
    layout: {
      x: layout.x + 14,
      y: layout.y + 12,
      width: layout.width - 28,
      height: 40,
    },
    zIndex: 5,
    notes: '流式增量追加；安卓用 TextView / Compose Text',
  };

  const bubbleRef = useComponentMeta<HTMLDivElement>(buildComponentMeta(bubbleMeta, 'container'));
  const textRef = useComponentMeta<HTMLParagraphElement>(buildComponentMeta(textMeta, 'text'));

  const roleClass =
    message.role === 'user'
      ? styles.bubbleUser
      : message.role === 'assistant'
        ? styles.bubbleAssistant
        : styles.bubbleSystem;

  return (
    <motion.li
      className={styles.messageRow}
      data-message-role={message.role}
      data-ui-id={`${baseId}.row`}
      data-status={message.status}
      variants={presets.listStagger.item}
    >
      {hasAvatar ? <MessageAvatar message={message} preset={rolePreset} layout={layout} /> : null}
      <div className={styles.messageContent}>
      <div
      ref={bubbleRef}
      className={`${styles.bubble} ${roleClass}`}
      data-ui-id={baseId}
      data-ui-role="container"
      data-status={message.status}
      onClick={(event) => {
        if (message.role !== 'user' || (event.target as HTMLElement).closest('button, a')) return;
        onToggleActions?.();
      }}
    >
      {showReasoning && !waitingForAudio && !hasVoice && message.reasoning !== undefined && message.reasoning !== '' ? (
        <ReasoningBlock
          baseId={baseId}
          layout={{
            x: layout.x + 12,
            y: layout.y + 8,
            width: layout.width - 24,
            height: 40,
          }}
          reasoning={message.reasoning}
          presets={presets}
        />
      ) : null}
      {waitingForAudio ? <AssistantVoicePending messageId={message.id} layout={layout} /> : hasVoice ? (
        <AssistantVoiceBubble messageId={message.id} text={message.content} duration={voiceReply?.duration ?? null} audioUrl={message.audioUrl} layout={layout} />
      ) : (
      <p
        ref={textRef}
        className={`${styles.bubbleText} ${imageGenerating ? styles.imageJobText : ''}`}
        data-ui-id={textMeta.id}
        data-ui-role="text"
        data-image-generating={imageGenerating ? 'true' : undefined}
        {...(imageGenerating ? { role: 'status' } : {})}
      >
        {message.content}
        {message.status === 'streaming' && !imageGenerating ? (
          <span className={styles.caret} aria-hidden="true" />
        ) : null}
      </p>
      )}
      {attachments.length > 0 ? (
        <MessageAttachments
          baseId={baseId}
          attachments={attachments}
          bubble={layout}
          onPreview={onPreview}
        />
      ) : null}
      {message.audioError ? (
        <ErrorBlock
          baseId={baseId}
          layout={{ x: layout.x + 12, y: layout.y + 44, width: layout.width - 24, height: 32 }}
          message={message.audioError}
        />
      ) : null}
      {notice === null ? null : (
        <ContextNotice
          baseId={baseId}
          layout={{ x: layout.x + 12, y: layout.y + 44, width: layout.width - 24, height: 44 }}
          summary={notice}
        />
      )}
      {citations !== undefined && citations.length > 0 ? (
        <CitationSources
          baseId={baseId}
          layout={{ ...layout, y: layout.y + (hasInjection ? 44 : 0) }}
          results={citations}
        />
      ) : null}
      {message.status === 'error' && message.imageJob === undefined ? (
        <ErrorBlock
          baseId={baseId}
          layout={{ x: layout.x + 12, y: layout.y + 44, width: layout.width - 24, height: 32 }}
          message={message.content === '' ? '生成失败' : '生成失败，请检查模型配置后重试'}
        />
      ) : null}
      {imageGenerating ? (
        <p className={styles.imageSending} role="status" data-ui-id={`${baseId}.imageSending`}>图片发送中</p>
      ) : null}
      {!waitingForAudio && !hasVoice && ttsEnabled && message.role === 'assistant' && message.status === 'done' && completed && message.content.trim() !== '' ? (
        <AssistantTtsButton
          messageId={message.id}
          text={message.content}
          layout={ttsButtonLayout(layout)}
        />
      ) : null}
      </div>
      {completed ? (
        <div className={styles.messageFooter} data-ui-id={`${baseId}.footer`} data-ui-role="container">
          <time className={styles.messageTime} data-ui-id={`${baseId}.timestamp`} data-ui-role="text" dateTime={new Date(message.createdAt).toISOString()} title={new Date(message.createdAt).toLocaleString('zh-CN')} aria-label="消息生成时间">
            {new Date(message.createdAt).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })}
          </time>
          <button type="button" className={styles.messageActionButton} data-ui-id={`${baseId}.copyButton`} aria-label={copyState === 'copied' ? '已复制消息' : '复制消息'} title={copyState === 'copied' ? '已复制消息' : '复制消息'} onClick={() => { void copyMessage(); }}>
            <AppIcon name={copyState === 'copied' ? 'check' : 'copy'} size={15} />
          </button>
          <button type="button" className={styles.messageActionButton} data-ui-id={`${baseId}.deleteButton`} aria-label="删除消息" title="删除消息" onClick={onDeleteMessage}>
            <AppIcon name="delete" size={15} />
          </button>
          {copyState === 'error' ? <span className={styles.copyNotice} role="status">复制失败，请重试</span> : null}
        </div>
      ) : null}
      {message.role === 'user' && actionsOpen && completed ? (
        <div className={styles.messageActions} role="dialog" aria-label="消息操作">
          <button type="button" className={styles.messageActionButton} data-ui-id={`${baseId}.regenerateButton`} aria-label="重新生成" title="重新生成" onClick={onRegenerateMessage}>
            <AppIcon name="refresh" size={15} />
          </button>
        </div>
      ) : null}
      </div>
    </motion.li>
  );
}

function MessageAvatar({ message, preset, layout }: {
  message: ChatMessage;
  preset: Preset | null;
  layout: ComponentMetaInput['layout'];
}): ReactElement {
  const isUser = message.role === 'user';
  const label = isUser ? '用户头像' : preset ? `编辑角色：${preset.name}` : '助手头像';
  const userAvatar = useUserAvatar();
  const avatar = isUser ? userAvatar : preset?.avatar?.trim();
  const [failedSource, setFailedSource] = useState<string | null>(null);
  const imageSource = avatar && /^(https?:\/\/|data:image\/|blob:|\/)/i.test(avatar) ? avatar : null;
  const editable = !isUser && preset !== null;
  const ref = useComponentMeta<HTMLButtonElement>(buildComponentMeta({
    id: `chat.message.${message.role}.${message.id}.avatar`,
    page: 'chat',
    role: editable ? 'button' : 'icon',
    label,
    layout: { x: isUser ? 330 : 16, y: layout.y, width: 44, height: 44 },
    zIndex: 5,
    notes: '消息侧边头像；角色头像点击进入角色编辑并返回当前聊天',
  }, editable ? 'button' : 'icon'));
  const content = imageSource && failedSource !== imageSource
    ? <img src={imageSource} alt="" onError={() => setFailedSource(imageSource)} />
    : avatar && !imageSource ? avatar
      : !isUser && preset?.name.trim() ? Array.from(preset.name.trim())[0]
        : <AppIcon name="contacts" size={22} />;
  const attributes = {
    ref,
    className: styles.messageAvatar,
    'data-ui-id': `chat.message.${message.role}.${message.id}.avatar`,
    'data-ui-role': editable ? 'button' : 'icon',
    'aria-label': label,
  };
  return editable ? (
    <button {...attributes} type="button" title={label}
      onClick={() => { window.location.hash = `/contacts?edit=${encodeURIComponent(preset.id)}&return=chat`; }}>
      {content}
    </button>
  ) : <span {...attributes} role="img">{content}</span>;
}

/** 没有活动会话时的空状态引导。 */
function ChatEmptyState({ presets }: { presets: MotionPresets }): ReactElement {
  const boxRef = useComponentMeta<HTMLDivElement>(chatComponentMeta('chat.empty'));
  const titleRef = useComponentMeta<HTMLHeadingElement>(chatComponentMeta('chat.empty.title'));
  const hintRef = useComponentMeta<HTMLParagraphElement>(chatComponentMeta('chat.empty.hint'));
  const actionRef = useComponentMeta<HTMLButtonElement>(
    chatComponentMeta('chat.empty.actionButton'),
  );
  const origin = { x: 0, y: PAGE_TOP };

  return (
    <>
      <div
        ref={boxRef}
        className={styles.emptyBox}
        style={boxStyle(chatLayout('chat.empty'), 3, origin)}
        data-ui-id="chat.empty"
        data-ui-role="container"
      />
      <h2
        ref={titleRef}
        className={styles.emptyTitle}
        style={boxStyle(chatLayout('chat.empty.title'), 4, origin)}
        data-ui-id="chat.empty.title"
      >
        还没有打开会话
      </h2>
      <p
        ref={hintRef}
        className={styles.emptyHint}
        style={boxStyle(chatLayout('chat.empty.hint'), 4, origin)}
        data-ui-id="chat.empty.hint"
      >
        去会话列表新建一条，再回到这里开始聊天
      </p>
      <motion.button
        ref={actionRef}
        type="button"
        className={styles.primaryButton}
        style={boxStyle(chatLayout('chat.empty.actionButton'), 4, origin)}
        data-ui-id="chat.empty.actionButton"
        aria-label="去会话列表新建"
        whileTap={presets.tap.whileTap}
        transition={presets.tap.transition}
        onClick={() => navigate('/messages')}
      >
        去会话列表
      </motion.button>
    </>
  );
}

/** 没有启用任何 provider 时的提示条（不崩溃，给出明确引导）。 */
function ProviderNotice({ presets }: { presets: MotionPresets }): ReactElement {
  const boxRef = useComponentMeta<HTMLDivElement>(chatComponentMeta('chat.providerNotice'));
  const messageRef = useComponentMeta<HTMLParagraphElement>(
    chatComponentMeta('chat.providerNotice.message'),
  );
  const actionRef = useComponentMeta<HTMLButtonElement>(
    chatComponentMeta('chat.providerNotice.actionButton'),
  );
  const origin = { x: 0, y: PAGE_TOP };

  return (
    <>
      <div
        ref={boxRef}
        className={styles.notice}
        style={boxStyle(chatLayout('chat.providerNotice'), 6, origin)}
        data-ui-id="chat.providerNotice"
        data-ui-role="container"
      />
      <p
        ref={messageRef}
        className={styles.noticeText}
        style={boxStyle(chatLayout('chat.providerNotice.message'), 7, origin)}
        data-ui-id="chat.providerNotice.message"
        role="status"
      >
        {NO_PROVIDER_HINT}
      </p>
      <motion.button
        ref={actionRef}
        type="button"
        className={styles.noticeButton}
        style={boxStyle(chatLayout('chat.providerNotice.actionButton'), 7, origin)}
        data-ui-id="chat.providerNotice.actionButton"
        aria-label="去设置页配置"
        whileTap={presets.tap.whileTap}
        transition={presets.tap.transition}
        onClick={() => navigate('/settings')}
      >
        去配置
      </motion.button>
    </>
  );
}

/** 错误提示条（发送失败 / 未选模型等）。 */
function ErrorBanner({ message }: { message: string }): ReactElement {
  const boxRef = useComponentMeta<HTMLDivElement>(chatComponentMeta('chat.errorBanner'));
  const messageRef = useComponentMeta<HTMLParagraphElement>(
    chatComponentMeta('chat.errorBanner.message'),
  );
  return (
      <div
        ref={boxRef}
        className={styles.errorBanner}
        data-ui-id="chat.errorBanner"
        data-ui-role="container"
      >
      <p
        ref={messageRef}
        className={styles.errorText}
        data-ui-id="chat.errorBanner.message"
        role="alert"
      >
        {message}
      </p>
      </div>
  );
}

/** 生成状态提示（当前只有「已停止生成」）。 */
function StatusHint({ message }: { message: string }): ReactElement {
  const boxRef = useComponentMeta<HTMLDivElement>(chatComponentMeta('chat.statusHint'));
  const messageRef = useComponentMeta<HTMLParagraphElement>(
    chatComponentMeta('chat.statusHint.message'),
  );
  const origin = { x: 0, y: PAGE_TOP };
  return (
    <>
      <div
        ref={boxRef}
        className={styles.statusHint}
        style={boxStyle(chatLayout('chat.statusHint'), 6, origin)}
        data-ui-id="chat.statusHint"
        data-ui-role="container"
      />
      <p
        ref={messageRef}
        className={styles.statusHintText}
        style={boxStyle(chatLayout('chat.statusHint.message'), 7, origin)}
        data-ui-id="chat.statusHint.message"
      >
        {message}
      </p>
    </>
  );
}

/** 生图成功后的「已存入图库」提示（点击跳图库页）。 */
function GalleryNotice({ message, onOpen }: { message: string; onOpen: () => void }): ReactElement {
  const presets = useMotionPresets();
  const ref = useComponentMeta<HTMLButtonElement>(chatComponentMeta('chat.image.galleryNotice'));
  return (
    <motion.button
      ref={ref}
      type="button"
      className={styles.galleryNotice}
      style={boxStyle(chatLayout('chat.image.galleryNotice'), 13)}
      data-ui-id="chat.image.galleryNotice"
      data-ui-role="button"
      aria-label={`${message}，点击查看图库`}
      whileTap={presets.tap.whileTap}
      transition={presets.tap.transition}
      onClick={onOpen}
    >
      {message}
    </motion.button>
  );
}

export interface ChatPageProps {
  /** 语音能力依赖注入（TTS / 转写适配器 / 录音器）；缺省使用默认依赖。 */
  voice?: VoiceDeps;
  /** 可注入的联网检索入口；默认取本地已配置的 provider（测试注入 fake，不联网）。 */
  searchRunner?: SearchRunner;
  /** 可注入的文件选择器；缺省走 T09 的 `pickAttachments()`（测试注入 fake，不弹系统选择器）。 */
  picker?: (options: { existingCount: number }) => Promise<PickResult>;
  /** 可注入的生图入口；缺省走 `createImageAdapter().generate()`（测试注入 fake，不联网）。 */
  generateImage?: ImageGenerator;
  /** 可注入的落图库入口；缺省走 T11b 的 `saveGeneratedImages()`。 */
  saveImages?: (
    images: readonly GeneratedImage[],
    options: SaveGeneratedImagesOptions,
  ) => Promise<SaveGeneratedImagesResult>;
}

export function ChatPage(props: ChatPageProps = {}): ReactElement {
  const [activeId, setActiveId] = useState(getActiveConversationId);
  return <ActiveChatPage key={activeId ?? 'empty'} {...props} onConversationChange={setActiveId} />;
}

function ActiveChatPage({
  voice,
  searchRunner = defaultSearchRunner,
  picker = pickAttachments,
  generateImage = defaultImageGenerator,
  saveImages = saveGeneratedImages,
  onConversationChange,
}: ChatPageProps & { onConversationChange: (id: string) => void }): ReactElement {
  const presets = useMotionPresets();
  const displayFeatures = useFeatureFlags();
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [presetList, setPresetList] = useState<Preset[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [providers, setProviders] = useState<ProviderConfig[]>([]);
  const selectedPreset = useMemo(() => presetList.find(item => item.id === conversation?.presetId) ?? null, [conversation?.presetId, presetList]);
  const roleResolution = useMemo(() => {
    try { return { conversation: resolveRoleConversation(selectedPreset ?? undefined, conversation ?? undefined, providers), error: null }; }
    catch (caught) { return { conversation: conversation ?? undefined, error: caught instanceof Error ? caught.message : '角色模型配置无效' }; }
  }, [conversation, providers, selectedPreset]);
  const effectiveConversation = roleResolution.conversation;
  const [officialModelOptions, setOfficialModelOptions] = useState<OfficialModelOption[]>([]);
  const [officialImageOptions, setOfficialImageOptions] = useState<OfficialImageOption[]>([]);
  const [savingSelection, setSavingSelection] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const switchingRef = useRef(false);
  const [officialLoading, setOfficialLoading] = useState(false);
  const [officialError, setOfficialError] = useState<string | null>(null);
  const officialRevision = useRef(0);
  useEffect(() => {
    if (conversation?.id) void messageRepo.markConversationRead(conversation.id);
  }, [conversation?.id]);
  const preferenceAppliedFor = useRef<string | null>(null);
  const syncedOfficialLimitsFor = useRef<string | null>(null);
  const officialModels = useMemo(
    () => officialModelOptions.map((option) => option.label),
    [officialModelOptions],
  );
  const officialSelectableModels = useMemo(
    () => [
      ...officialModelOptions.map((option) => option.label),
      ...officialImageOptions.map((option) => `${option.label}（生图）`),
    ],
    [officialImageOptions, officialModelOptions],
  );
  const selectedProviderId = effectiveConversation?.providerId ?? '';
  const selectedProvider = providers.find(provider => provider.id === selectedProviderId) ?? null;
  const catalog = useProviderModels(selectedProvider);
  const officialSelected = selectedProviderId === SERVER_PROVIDER_ID;
  const currentModel = effectiveConversation?.model ?? '';
  const currentModelKind: 'text' | 'image' =
    officialSelected
      ? (officialImageOptions.some((option) => option.slug === officialModelId(currentModel))
          ? 'image'
          : 'text')
      : selectedProvider === null
        ? (conversation?.modelKind ?? 'text')
        : (providerPurpose(selectedProvider) === 'image' ? 'image' : 'text');
  const selectableModels = [...new Set([
    ...(officialSelected ? officialSelectableModels : catalog.models),
    ...(officialSelected ? [] : [selectedProvider?.defaultModel ?? '', currentModel]),
    ...(officialSelected ? [currentModel] : []),
  ].filter(Boolean))];
  const modelsLoading = officialSelected ? officialLoading : catalog.loading;
  const modelsError = officialSelected ? officialError : catalog.error;
  const [loaded, setLoaded] = useState(false);
  const [input, setInput] = useState('');
  const [selectedUserMessageId, setSelectedUserMessageId] = useState<string | null>(null);
  const [inputHeight, setInputHeight] = useState(chatLayout('chat.composer.input').height);
  const composerGrowth = inputHeight - chatLayout('chat.composer.input').height;
  const composerHeight = chatLayout('chat.composer.bar').height + composerGrowth;
  const composerButtonOrigin = { x: 0, y: COMPOSER_TOP - composerGrowth };
  const [streaming, setStreaming] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchEnabled, setSearchEnabled] = useState<boolean>(() => loadSearchEnabled());
  const [searchNotice, setSearchNotice] = useState<string | null>(null);
  const [citations, setCitations] = useState<Record<string, SearchResult[]>>({});
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [galleryNotice, setGalleryNotice] = useState<string | null>(null);
  const [preview, setPreview] = useState<Attachment | null>(null);
  const [memories, setMemories] = useState<MemoryEntry[]>([]);
  const [injections, setInjections] = useState<Record<string, ContextInjectionSummary>>({});

  useEffect(() => {
    setInput(readDraft(conversation?.id));
  }, [conversation?.id]);

  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);

  const [ttsAccount, setTtsAccount] = useState<TtsAccount | null>(null);
  const [ttsMode, setTtsMode] = useState<TtsMode>('off');
  const [waitingAudio, setWaitingAudio] = useState<Record<string, boolean>>({});
  const [voiceReplies, setVoiceReplies] = useState<Record<string, { duration: number | null }>>({});
  const voiceDisplay = loadVoiceDisplayPreferences(ttsAccount?.userId ?? (voice ? 'injected-voice' : undefined));
  const voiceSelectionKey = voice ? undefined : JSON.stringify([ttsMode, ttsAccount?.preferences, selectedPreset]);
  const ttsSelection = useRef({ account: ttsAccount, mode: ttsMode });
  const audioDeliveryEpoch = useRef(0);
  ttsSelection.current = { account: ttsAccount, mode: ttsMode };
  const voiceDeps = useMemo<VoiceDeps>(() => voice ?? {
    ...createDefaultVoiceDeps(),
    tts: modelGateway.createOfficialTtsAdapter({
      getSelection: () => {
        const current = ttsSelection.current;
        if (!current.account) throw new Error('Authentication required');
        return { userId: current.account.userId, mode: current.mode, preferences: current.account.preferences };
      },
      getMessageId: (request) => request.messageId,
      getLocalVoice: loadMessageRoleVoice,
    }),
  }, [voice]);
  const voiceContext = useVoiceController({
    deps: voiceDeps,
    onTranscript: (text: string) => {
      setInput(text);
      writeDraft(conversation?.id ?? getActiveConversationId(), text);
    },
  });
  const voiceController = voiceContext.controller;
  useEffect(() => {
    if (voice) return;
    let alive = true;
    let revision = 0;
    const refresh = (): void => {
      const current = ++revision;
      void mimoClient.account().then((account) => {
        if (alive && current === revision) setTtsAccount(account);
      }).catch(() => {
        if (alive && current === revision) { audioDeliveryEpoch.current++; setTtsAccount(null); setTtsMode('off'); voiceController.clearPreparedAudio(); setWaitingAudio({}); setVoiceReplies({}); }
      });
    };
    refresh();
    window.addEventListener('focus', refresh);
    return () => { alive = false; window.removeEventListener('focus', refresh); };
  }, [voice, voiceController]);
  useEffect(() => {
    if (!voice && (ttsMode === 'off' || !ttsAccount)) {
      voiceController.setVoiceMode(false);
      voiceController.stopPlayback();
    }
  }, [voice, ttsMode, ttsAccount, voiceController]);

  useEffect(() => {
    audioDeliveryEpoch.current++;
    voiceController.clearPreparedAudio(); setWaitingAudio({}); setVoiceReplies({});
  }, [ttsAccount?.userId, voiceSelectionKey, voiceController]);

  const rootRef = useComponentMeta<HTMLElement>(chatComponentMeta('page.chat.root'));
  const headerRef = useComponentMeta<HTMLDivElement>(chatComponentMeta('chat.header'));
  const backRef = useComponentMeta<HTMLButtonElement>(chatComponentMeta('chat.header.backButton'));
  const newConversationRef = useComponentMeta<HTMLButtonElement>(chatComponentMeta('chat.header.newConversation'));
  const sessionsRef = useComponentMeta<HTMLButtonElement>(chatComponentMeta('chat.header.sessions'));
  const galleryRef = useComponentMeta<HTMLButtonElement>(
    chatComponentMeta('chat.header.galleryButton'),
  );
  const titleRef = useComponentMeta<HTMLHeadingElement>(chatComponentMeta('chat.header.title'));
  const modelSelectRef = useComponentMeta<HTMLSelectElement>(
    chatComponentMeta('chat.header.modelSelect'),
  );
  const providerSelectRef = useComponentMeta<HTMLSelectElement>(chatComponentMeta('chat.header.providerSelect'));
  const refreshModelsRef = useComponentMeta<HTMLButtonElement>(chatComponentMeta('chat.header.refreshModels'));
  const roleModelRef = useComponentMeta<HTMLButtonElement>(chatComponentMeta('chat.controls.roleModel'));
  const messagesRef = useComponentMeta<HTMLDivElement>(chatComponentMeta('chat.messages'));
  const composerRef = useComponentMeta<HTMLDivElement>(chatComponentMeta('chat.composer.bar'));
  const inputRef = useComponentMeta<HTMLTextAreaElement>(chatComponentMeta('chat.composer.input'));
  useLayoutEffect(() => {
    const element = inputRef.current;
    if (!element) return;
    const minHeight = chatLayout('chat.composer.input').height;
    const maxHeight = Number.parseFloat(getComputedStyle(element).maxHeight) || minHeight;
    // Reset before measuring so deleting text, sending and voice transcription also resize.
    element.style.height = `${minHeight}px`;
    const height = Math.max(minHeight, Math.min(maxHeight, input ? element.scrollHeight : minHeight));
    element.style.height = `${height}px`;
    setInputHeight(height);
  }, [input, inputRef]);
  const attachRef = useComponentMeta<HTMLButtonElement>(
    chatComponentMeta('chat.composer.attachButton'),
  );
  const sendRef = useComponentMeta<HTMLButtonElement>(
    chatComponentMeta('chat.composer.sendButton'),
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const conversationId = conversation?.id;
    if (!conversationId) return;
    const subscription = liveQuery(() => messageRepo.listMessages(conversationId)).subscribe(history => {
      if (!mountedRef.current) return;
      history = history.map(message => {
        if (!message.roleVoicePending || message.backgroundJob || activeChatController(message.id) || roleVoiceDeliveryActive(message.id)) return message;
        const patch = { roleVoicePending: false,
          ...(['pending', 'streaming'].includes(message.status) ? { status: 'error' as const } : {}) };
        void messageRepo.updateMessage(message.id, patch).catch(() => undefined);
        return { ...message, ...patch };
      });
      setMessages(history);
      const running = history.find(message => message.role === 'assistant' && (
        ['pending', 'streaming'].includes(message.status) || Boolean(activeChatController(message.id))
        || (message.backgroundJob && !message.backgroundJob.saved && !message.audioError)
      ));
      setStreaming(Boolean(running));
      abortRef.current = running
        ? (running.imageJob !== undefined
            ? activeImageJobController(running.id) ?? null
            : activeChatController(running.id) ?? null)
        : null;
    });
    return () => subscription.unsubscribe();
  }, [conversation?.id]);

  useEffect(() => {
    const conversationId = conversation?.id;
    if (!conversationId) return;
    const handler = (event: Event): void => {
      const detail = (event as CustomEvent<{ conversationId?: string; saved?: number; warning?: string }>).detail;
      if (detail?.conversationId !== conversationId) return;
      const saved = detail.saved ?? 0;
      const text = saved > 0 ? `已存入图库（${String(saved)} 张）` : '本次未存入图库';
      setGalleryNotice(detail.warning === undefined ? text : `${text}：${detail.warning}`);
    };
    window.addEventListener(IMAGE_JOB_FINISHED_EVENT, handler);
    return () => window.removeEventListener(IMAGE_JOB_FINISHED_EVENT, handler);
  }, [conversation?.id]);

  useEffect(() => {
    let alive = true;
    void (async (): Promise<void> => {
      let enabled: ProviderConfig[] = [];
      try {
        enabled = await providerRepo.listProviders({ enabled: true });
      } catch {
        enabled = [];
      }

      let nextOfficialOptions: OfficialModelOption[] = [];
      let nextOfficialImageOptions: OfficialImageOption[] = [];
      try {
        const officialCatalog = await loadOfficialCatalog();
        nextOfficialOptions = officialCatalog.chat;
        nextOfficialImageOptions = officialCatalog.image;
      } catch {
        nextOfficialOptions = [];
        nextOfficialImageOptions = [];
      }

      let activeConversation: Conversation | null = null;
      let history: ChatMessage[] = [];
      const activeId = getActiveConversationId();
      if (activeId !== null) {
        try {
          activeConversation = (await conversationRepo.getConversation(activeId)) ?? null;
          if (activeConversation !== null) history = await messageRepo.listMessages(activeId);
        } catch {
          activeConversation = null;
          history = [];
        }
      }

      let nextPresets: Preset[] = [];
      let nextMemories: MemoryEntry[] = [];
      try {
        nextPresets = await presetRepo.listPresets();
      } catch {
        nextPresets = [];
      }
      try {
        nextMemories = await memoryRepo.listMemories();
      } catch {
        nextMemories = [];
      }

      if (!alive) return;
      setProviders(enabled);
      setOfficialModelOptions(nextOfficialOptions);
      setOfficialImageOptions(nextOfficialImageOptions);
      setPresetList(nextPresets);
      setMemories(nextMemories);
      setConversation(activeConversation);
      setMessages(history);
      setLoaded(true);
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!loaded || conversation === null) return;
    if (selectedPreset?.modelConfig !== undefined) return;
    if (conversation.providerId !== undefined || conversation.model !== undefined) return;
    if (preferenceAppliedFor.current === conversation.id) return;
    preferenceAppliedFor.current = conversation.id;
    const preference = loadModelPreference();
    if (preference === null) return;
    if (
      preference.providerId !== SERVER_PROVIDER_ID &&
      !providers.some((provider) => provider.id === preference.providerId)
    ) return;
    void conversationRepo.updateConversation(conversation.id, preference).then((updated) => {
      if (updated !== undefined && mountedRef.current) setConversation(updated);
    });
  }, [conversation, loaded, providers, selectedPreset]);

  useEffect(() => {
    const box = messagesRef.current;
    if (box !== null) box.scrollTop = box.scrollHeight;
  }, [messages, messagesRef]);

  /** 会话首次绑定角色后保持角色身份，后续模型配置在角色卡中调整。 */
  const handleSelectPreset = useCallback(
    async (presetId: string | null): Promise<void> => {
      if (conversation === null || conversation.presetId != null || streaming || savingSelection) return;
      const selected = presetId === null ? null : presetList.find((item) => item.id === presetId) ?? null;
      if (presetId !== null && selected === null) {
        setError('所选角色卡已不存在，请刷新页面后重试');
        return;
      }
      const usable = selected !== null && (
        selected.systemPrompt.trim() !== '' ||
        (selected.description?.trim() ?? '') !== '' ||
        (selected.worldBook ?? []).some((entry) => entry.enabled === true && entry.content.trim() !== '')
      );
      setError(usable || selected === null ? null : '这张角色卡没有系统提示词或世界书，AI 无法按卡内设定回答；请编辑角色卡或导入角色卡文件。');
      try {
        const updated = await conversationRepo.updateConversation(conversation.id, {
          presetId: presetId ?? undefined,
        });
        if (updated !== undefined && mountedRef.current) setConversation(updated);
      } catch {
        setError('角色卡设置保存失败');
      }
    },
    [conversation, presetList, streaming, savingSelection],
  );

  const applyOfficialLimits = useCallback((model: string): void => {
    const option = officialModelOptions.find((item) => item.slug === officialModelId(model));
    if (!option) return;
    const current = loadModelSettings();
    if (current.maxContext !== option.contextTokens || current.maxOutputTokens !== option.maxOutputTokens) {
      updateModelSettings({
        maxContext: option.contextTokens,
        maxOutputTokens: option.maxOutputTokens,
      });
    }
  }, [officialModelOptions]);

  const saveModelSelection = useCallback(
    async (providerId: string, model: string): Promise<void> => {
      if (conversation === null || streaming || savingSelection) return;
      if (selectedPreset?.modelConfig !== undefined) return;
      setSavingSelection(true);
      try {
        const officialImage =
          providerId === SERVER_PROVIDER_ID &&
          officialImageOptions.some((option) => option.slug === officialModelId(model));
        const selected = providers.find((provider) => provider.id === providerId);
        const modelKind =
          providerId === SERVER_PROVIDER_ID
            ? (officialImage ? 'image' : 'text')
            : selected === undefined
              ? 'text'
              : (providerPurpose(selected) === 'image' ? 'image' : 'text');
        const updated = await conversationRepo.updateConversation(conversation.id, {
          providerId, model, modelKind,
        });
        if (updated !== undefined) {
          if (providerId !== '' && model !== '') {
            saveModelPreference(providerId, model, modelKind);
            if (providerId === SERVER_PROVIDER_ID && modelKind === 'text') applyOfficialLimits(model);
          }
          if (mountedRef.current) setConversation(updated);
        }
      } catch {
        setError('模型选择保存失败');
      } finally {
        if (mountedRef.current) setSavingSelection(false);
      }
    },
    [applyOfficialLimits, conversation, officialImageOptions, providers, streaming, savingSelection, selectedPreset],
  );

  useEffect(() => {
    if (!loaded || conversation?.providerId !== SERVER_PROVIDER_ID) return;
    if (selectedPreset?.modelConfig !== undefined) return;
    if ((conversation.messageCount ?? 0) > 0) return;
    const option = officialModelOptions.find(
      (item) => item.slug === officialModelId(conversation.model ?? ''),
    );
    if (!option) return;
    const key = `${conversation.id}:${option.slug}`;
    if (syncedOfficialLimitsFor.current === key) return;
    syncedOfficialLimitsFor.current = key;
    applyOfficialLimits(option.label);
  }, [applyOfficialLimits, conversation, loaded, officialModelOptions, selectedPreset]);

  const refreshOfficialModels = useCallback(async (): Promise<void> => {
    const revision = ++officialRevision.current;
    setOfficialLoading(true); setOfficialError(null);
    try {
      const officialCatalog = await loadOfficialCatalog();
      if (mountedRef.current && revision === officialRevision.current) {
        setOfficialModelOptions(officialCatalog.chat);
        setOfficialImageOptions(officialCatalog.image);
      }
    } catch {
      if (mountedRef.current && revision === officialRevision.current) setOfficialError('官方模型列表加载失败，请登录后刷新');
    } finally {
      if (mountedRef.current && revision === officialRevision.current) setOfficialLoading(false);
    }
  }, []);

  const handleInputChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    const value = event.target.value;
    setInput(value);
    writeDraft(conversation?.id ?? getActiveConversationId(), value);
  };

  const startImageMessage = useCallback(
    async (input: {
      prompt: string;
      providerId: string;
      model: string;
      config: ResolvedImageConfig | null;
      serverModel: string | null;
      attachments?: readonly Attachment[];
      history?: ChatMessage[];
      clearComposer?: boolean;
    }): Promise<boolean> => {
      if (conversation === null) {
        setError('请先在会话列表新建一个会话');
        return false;
      }
      if (input.config === null && input.serverModel === null) {
        setError('当前选择的生图模型不可用');
        return false;
      }
      const history = input.history ?? messages;
      if (input.clearComposer === true) {
        setInput('');
        clearDraft(conversation.id);
        setAttachments([]);
      }
      setError(null);
      setCancelled(false);
      setSearchNotice(null);
      setGalleryNotice(null);
      setStreaming(true);

      let userMessage: ChatMessage;
      let assistant: ChatMessage;
      try {
        userMessage = await messageRepo.createAndSaveMessage({
          conversationId: conversation.id,
          role: 'user',
          content: input.prompt,
          status: 'done',
        });
        if ((input.attachments ?? []).length > 0) {
          const persisted = await persistAttachments(userMessage.id, input.attachments ?? []);
          if (persisted.message !== undefined) userMessage = persisted.message;
          if (persisted.warning !== undefined) setError(persisted.warning);
        }
        assistant = await messageRepo.createAndSaveMessage({
          conversationId: conversation.id,
          role: 'assistant',
          content: `正在生成图片…（${input.model}）`,
          status: 'streaming',
          imageJob: {
            prompt: input.prompt,
            providerId: input.providerId,
            model: input.model,
            serverModel: input.serverModel,
            createdAt: Date.now(),
          },
        });
      } catch {
        setStreaming(false);
        setError('消息写入本地失败');
        return false;
      }

      if (mountedRef.current) setMessages([...history, userMessage, assistant]);
      startImageJob({
        messageId: assistant.id,
        prompt: input.prompt,
        providerId: input.providerId,
        model: input.model,
        config: input.config,
        serverModel: input.serverModel,
        generateImage,
        saveImages,
      });
      abortRef.current = activeImageJobController(assistant.id) ?? null;

      try {
        const count = await messageRepo.countMessages(conversation.id);
        const title =
          conversation.title === NEW_CONVERSATION_TITLE
            ? deriveTitle(input.prompt)
            : conversation.title;
        const updated = await conversationRepo.updateConversation(conversation.id, {
          messageCount: count,
          title,
        });
        if (updated !== undefined && mountedRef.current) setConversation(updated);
      } catch {
        // The image job still owns the message and will update the count when done.
      }
      return true;
    },
    [conversation, generateImage, messages, saveImages],
  );

  const handleSend = useCallback(async (override?: { text?: string; attachments?: Attachment[]; history?: ChatMessage[] }): Promise<void> => {
    if (streaming || savingSelection) return;
    const isOverride = override !== undefined;
    const text = (override?.text ?? input).trim();
    const pendingAttachments = override?.attachments ?? attachments;
    if (conversation === null) {
      setError('请先在会话列表新建一个会话');
      return;
    }
    // 只发附件不发文字也应可发送：文字与附件至少有一个。
    if (text === '' && pendingAttachments.length === 0) return;

    if (roleResolution.error || !effectiveConversation) {
      setError(roleResolution.error ?? '角色模型配置无效');
      return;
    }
    const officialSelected = effectiveConversation.providerId === SERVER_PROVIDER_ID;
    const provider = officialSelected ? null : resolveProvider(effectiveConversation, providers);
    if (!officialSelected && provider === null) {
      setError(`${NO_PROVIDER_HINT}，请到设置页添加并启用`);
      return;
    }
    const model = (effectiveConversation.model ?? provider?.defaultModel ?? '').trim();
    if (model === '') {
      setError('请在底部会话菜单选择一个模型');
      return;
    }
    const officialModelKnown =
      officialModelOptions.some((option) => option.label === model || option.slug === officialModelId(model)) ||
      officialImageOptions.some((option) => option.label === model || option.slug === officialModelId(model));
    if (officialSelected && !officialModelKnown) {
      setError('官方模型已下架或暂不可用');
      return;
    }
    if (selectedPreset?.modelConfig !== undefined && officialSelected) {
      const option = officialModelOptions.find(row => row.slug === officialModelId(model));
      if (!option) { setError('角色模型必须选择可用的官方文字模型'); return; }
      try { validateRoleModelLimits(selectedPreset.modelConfig, option); }
      catch (caught) { setError(caught instanceof Error ? caught.message : '角色模型参数超出上限'); return; }
    }

    const officialImageSelected =
      officialSelected &&
      officialImageOptions.some((option) => option.slug === officialModelId(model));
    const imageMode = officialSelected
      ? officialImageSelected
      : provider !== null && providerPurpose(provider) === 'image';
    if (imageMode) {
      if (text === '') {
        setError('请输入生图提示词');
        return;
      }
      if (!officialSelected && (provider === null || !providerSupportsImage(provider))) {
        setError('当前提供商不支持图片生成，请选择 OpenAI 兼容提供商或官方生图模型');
        return;
      }
      const imageProviderId = officialSelected ? SERVER_PROVIDER_ID : provider!.id;
      const imageModel = officialSelected ? officialModelId(model) : model;
      const entry = resolveSelectedImageEntry({
        providerId: imageProviderId,
        model: imageModel,
        providers,
        officialOptions: officialImageOptions,
      });
      if (entry.config === null && entry.serverModel === null) {
        setError(entry.hint);
        return;
      }
      await startImageMessage({
        prompt: text,
        providerId: imageProviderId,
        model: imageModel,
        config: entry.config,
        serverModel: entry.serverModel,
        attachments: pendingAttachments,
        ...(override?.history === undefined ? {} : { history: override.history }),
        clearComposer: !isOverride,
      });
      return;
    }

    // 提供商配置里只存 `apiKeyRef`（明文密钥不进库），发请求前必须把明文解析出来
    // 传给适配器，否则不会带 Authorization 头 —— 服务端 401/403，用户看到的就是
    // 「发不出消息」。注意：解析不到密钥时**不阻断**，因为本地推理服务
    // （Ollama / LM Studio 等）本来就不需要密钥；那种情况下由服务端返回明确错误。
    let adapter: ProviderAdapter;
    let chatProvider: ResolvedProviderConfig;
    try {
      if (officialSelected) {
        chatProvider = SERVER_PROVIDER_CONFIG;
        adapter = modelGateway.createChatAdapter(chatProvider);
      } else {
        const apiKey = readSecret(provider!.apiKeyRef);
        const resolvedProvider =
          apiKey === null || apiKey.trim() === '' ? provider! : { ...provider!, apiKey };
        chatProvider = resolvedProvider;
        adapter = modelGateway.createChatAdapter(chatProvider);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '创建模型适配器失败');
      return;
    }

    const settings = selectedPreset?.modelConfig === undefined ? loadModelSettings() : effectiveConversation.settings;
    const automationAccountId = getMomentAccountId();
    setError(null);
    setCancelled(false);
    setSearchNotice(null);
    if (!isOverride) {
      setInput('');
      clearDraft(conversation.id);
    }
    setAttachments([]);

    const controller = new AbortController();
    abortRef.current = controller;
    setStreaming(true);

    // T12：开关开启时先检索，结果以 system 上下文注入本次请求；失败 / 空结果不阻断对话。
    const searchOutcome: SearchOutcome = await executeSearch({
      enabled: searchEnabled,
      query: text,
      runner: searchRunner,
      signal: controller.signal,
    });
    if (mountedRef.current && searchOutcome.message !== undefined) setSearchNotice(searchOutcome.message);
    // 搜索途中被取消：还原输入并结束本次发送，不产生半截消息。
    if (controller.signal.aborted) {
      abortRef.current = null;
      if (!isOverride) {
        setInput(text);
        writeDraft(conversation.id, text);
      }
      setAttachments(pendingAttachments);
      setStreaming(false);
      return;
    }

    const history = override?.history ?? messages;
    let userMessage: ChatMessage;
    let draft: ChatMessage;
    try {
      userMessage = await messageRepo.createAndSaveMessage({
        conversationId: conversation.id,
        role: 'user',
        content: text,
        status: 'done',
      });
      // T23：附件随用户消息落库（复用 T09 的 persistAttachments；超限时给出可见提示）。
      if (pendingAttachments.length > 0) {
        const persisted = await persistAttachments(userMessage.id, pendingAttachments);
        if (persisted.message !== undefined) userMessage = persisted.message;
        if (persisted.warning !== undefined) setError(persisted.warning);
      }
      draft = await messageRepo.createAndSaveMessage({
        conversationId: conversation.id,
        role: 'assistant',
        content: '',
        status: 'pending',
        readAt: Date.now(),
      });
    } catch {
      setError('消息写入本地失败');
      if (!isOverride) {
        setInput(text);
        writeDraft(conversation.id, text);
      }
      setStreaming(false);
      abortRef.current = null;
      return;
    }
    const releaseRun = registerChatRun(draft.id, controller);

    const audioEnabled = voice !== undefined || (ttsMode !== 'off' && ttsAccount !== null);
    const delayText = audioEnabled && voiceDisplay.waitForAudio;
    const deliveryUserId = ttsAccount?.userId ?? (voice ? 'injected-voice' : '');
    const deliveryEpoch = audioDeliveryEpoch.current;
    if (delayText) setWaitingAudio(current => ({ ...current, [draft.id]: true }));
    if (mountedRef.current) setMessages([...history, userMessage, draft]);
    if (searchOutcome.status === 'ok') {
      setCitations((prev) => ({ ...prev, [draft.id]: searchOutcome.results }));
    }
    setStreaming(true);

    let rawContent = '';
    let content = '';
    let reasoning = '';
    let failure: string | null = null;

    const roleCanSendVoice = selectedPreset?.autoTtsEnabled === true
      && getEntitlements().active && getEntitlements().capabilities.autoTts;
    const streamingSaved = await messageRepo.updateMessage(draft.id, { status: 'streaming', roleVoicePending: roleCanSendVoice });
    if (streamingSaved !== undefined && mountedRef.current) {
      setMessages((prev) => prev.map((item) => (item.id === draft.id ? streamingSaved : item)));
    }

    // T24：角色卡 / 世界书 → 记忆召回 → 搜索引用（T12）按固定顺序组装成首条 system；
    // 组装结果只进请求上下文，不落库。注入摘要按助手消息 id 记录，供信息条追溯。
    const personalProfile = await loadActiveProfile().catch(() => null);
    const requestMessages = assembleChatMessages({
      conversationId: conversation.id,
      messages: [...history, userMessage],
      preset: selectedPreset,
      memories,
      memoryInjectionMode: loadMemoryInjectionMode(),
      personalProfile,
      searchResults: searchOutcome.status === 'ok' ? searchOutcome.results : [],
    });
    const automationEntitlements = getEntitlements();
    const roleCanGenerateImage = selectedPreset !== null
      && automationEntitlements.active && automationEntitlements.capabilities.autoImage
      && roleImageAvailable(selectedPreset, { providers, officialImageOptions });
    const needsInitiative = selectedPreset !== null && ((automationEntitlements.active && automationEntitlements.capabilities.autoText
      && (automationAllowsBubble(selectedPreset.automationMode) || automationAllowsMoment(selectedPreset.automationMode))) || roleCanGenerateImage
      || (selectedPreset.autoTtsEnabled === true && automationEntitlements.active && automationEntitlements.capabilities.autoTts));
    const automationFeatures = needsInitiative ? await loadFeatureFlags(true) : getFeatureFlags();
    const roleTools = selectedPreset === null ? [] : buildRoleTools(selectedPreset, {
      autoText: automationEntitlements.active && automationEntitlements.capabilities.autoText,
      autoImage: roleCanGenerateImage,
      autoTts: automationEntitlements.active && automationEntitlements.capabilities.autoTts,
    }, automationFeatures);
    if (selectedPreset !== null) {
      const entitlements = automationEntitlements;
      const instruction = buildAutomationInstructions(selectedPreset, {
        autoText: entitlements.active && entitlements.capabilities.autoText,
        autoImage: roleCanGenerateImage,
        autoTts: entitlements.active && entitlements.capabilities.autoTts,
      }, automationFeatures);
      if (instruction !== '') {
        if (requestMessages.systemMessage) {
          requestMessages.systemMessage.content = `${requestMessages.systemMessage.content}\n\n${instruction}`;
          const first = requestMessages.messages[0];
          if (first?.role === 'system') requestMessages.messages[0] = { ...first, content: requestMessages.systemMessage.content };
        } else {
          const systemMessage: ChatMessage = {
            id: newId(),
            conversationId: conversation.id,
            role: 'system',
            content: instruction,
            createdAt: Date.now(),
            status: 'done',
          };
          requestMessages.messages.unshift(systemMessage);
          requestMessages.systemMessage = systemMessage;
        }
      }
    }
    if (requestMessages.summary.injected && mountedRef.current) {
      setInjections((prev) => ({ ...prev, [draft.id]: requestMessages.summary }));
    }
    // 用户上下文是本次请求的完整 system 提示词；有注入时清除设置回退值，
    // 让所有 Provider（包括第三方/后台任务适配器）都无法再次拼入默认提示词。
    const requestSettings = requestMessages.systemMessage === null
      ? settings
      : { ...settings, systemPrompt: undefined };
    const toolCallDeltas = new Map<number, StreamToolCall>();

    try {
      const intimacy = selectedPreset ? await buildIntimacyContext(selectedPreset.id, controller.signal) : undefined;
      const chatRequest = applyRoleContextBudget({
        messages: requestMessages.messages,
        settings: requestSettings,
        model: officialSelected ? officialModelId(model) : model,
        signal: controller.signal,
        ...(roleTools.length > 0 ? { tools: roleTools } : {}),
        ...(intimacy ? { intimacy } : {}),
      }, selectedPreset);
      if (backgroundChatEnabled()) adapter = createBackgroundChatAdapter(chatProvider, draft.id, chatRequest);
      const iterator = filterGeneratedChunks(adapter.chat(chatRequest));
      for await (const chunk of iterator) {
        if (chunk.type === 'text') {
          rawContent += chunk.delta ?? '';
          content = stripControlMarkers(rawContent);
        } else if (chunk.type === 'reasoning') {
          reasoning += chunk.delta ?? '';
        } else if (chunk.type === 'error') {
          failure = `${formatProviderError(chunk)}（当前模型：${model}）`;
          break;
        } else if (chunk.type === 'done') {
          break;
        } else if (chunk.type === 'tool_call') {
          mergeToolCallDelta(toolCallDeltas, chunk.delta ?? '');
          continue;
        } else {
          continue;
        }
        await messageRepo.updateMessage(draft.id, { content, ...(reasoning === '' ? {} : { reasoning }) });
        if (mountedRef.current) {
          setMessages((prev) =>
            prev.map((item) =>
              item.id === draft.id
                ? { ...item, content, ...(reasoning === '' ? {} : { reasoning }) }
                : item,
            ),
          );
        }
      }
    } catch (caught) {
      if (!controller.signal.aborted) {
        failure = caught instanceof Error ? caught.message : '模型调用失败';
      }
    }

    const parsedOutput = applyControlToolCalls(
      parseRichOutput(rawContent),
      completedToolCalls(toolCallDeltas),
    );
    content = parsedOutput.text;
    const finalPatch: Partial<Omit<ChatMessage, 'id' | 'conversationId'>> = {
      content,
      status: failure === null ? 'done' : 'error',
      ...(parsedOutput.ttsRequested ? { roleVoiceRequested: true } : {}),
      ...(failure !== null || controller.signal.aborted ? { roleVoicePending: false } : {}),
      ...(reasoning === '' ? {} : { reasoning }),
    };
    const completedMessage = await messageRepo.updateMessage(draft.id, finalPatch);
    let richAudioAdded = false;
    const deliverMedia = async (): Promise<boolean> => {
      if (failure !== null || controller.signal.aborted || completedMessage === undefined || selectedPreset === null) return true;
      try {
        let output = parsedOutput;
        let decisionError: string | undefined;
        if (!output.ttsRequested && selectedPreset.autoTtsEnabled && automationEntitlements.active && automationEntitlements.capabilities.autoTts) {
          try {
            const requested = await decideRoleVoice({
              preset: selectedPreset,
              message: completedMessage,
              context: [...requestMessages.messages, completedMessage],
              adapter: modelGateway.createChatAdapter(chatProvider),
              model: officialSelected ? officialModelId(model) : model,
              settings: requestSettings,
              features: automationFeatures,
              signal: controller.signal,
            });
            if (controller.signal.aborted) return true;
            await messageRepo.updateMessage(draft.id, { roleVoiceRequested: requested, ...(!requested ? { roleVoicePending: false } : {}) });
            output = { ...output, ttsRequested: requested };
          } catch (error) {
            if (controller.signal.aborted) return true;
            decisionError = error instanceof Error ? error.message : '语音发送决策失败';
            await messageRepo.updateMessage(draft.id, { audioError: decisionError });
          }
        }
        const rich = await applyRichOutput(selectedPreset, completedMessage, output, {
          providers,
          expectedAccountId: automationAccountId,
          officialImageOptions,
          signal: controller.signal,
          generateImage,
          saveImages,
        });
        richAudioAdded = rich.audioAdded;
        const errors = [...(decisionError ? [decisionError] : []), ...rich.errors];
        if (errors.length > 0) setError(errors.join('；'));
        return errors.length === 0;
      } catch (richError) {
        const audioError = richError instanceof Error ? richError.message : '自动媒体处理失败';
        if (!controller.signal.aborted) {
          await messageRepo.updateMessage(draft.id, { audioError });
          setError(audioError);
        }
        return false;
      }
    };
    try {
      if (completedMessage) await acknowledgeChatJob(completedMessage, deliverMedia);
    } finally {
      try { await messageRepo.updateMessage(draft.id, { roleVoicePending: false }); }
      finally { releaseRun(); abortRef.current = null; }
    }
    const saved = await messageRepo.getMessage(draft.id);
    const aborted = controller.signal.aborted;

    if (mountedRef.current) {
      if (saved !== undefined) {
        setMessages((prev) => prev.map((item) => (item.id === draft.id ? saved : item)));
      }
      setStreaming(Boolean(saved?.backgroundJob && !saved.backgroundJob.saved && !saved.audioError));
      setCancelled(aborted && failure === null);
      if (failure !== null) setError(failure);
    }

    // Generate and save audio before revealing a delayed reply. Playback reuses the prepared audio.
    if (!richAudioAdded && failure === null && !aborted && saved?.status === 'done' && !saved.audioError && saved.content.trim() !== '' && delayText && deliveryEpoch === audioDeliveryEpoch.current) {
      void (async () => {
        try {
          const duration = await voiceController.prepareMessage(saved.id, saved.content);
          if (!mountedRef.current || deliveryEpoch !== audioDeliveryEpoch.current) return;
          rememberVoiceReply(deliveryUserId, saved.id, duration, voiceSelectionKey);
          if (voiceDisplay.voiceBubble) setVoiceReplies(current => ({ ...current, [saved.id]: { duration } }));
          void voiceController.autoSpeakAssistant(saved.id, saved.content);
        } catch {
          if (mountedRef.current && deliveryEpoch === audioDeliveryEpoch.current) setError('语音生成失败，已显示回复文字');
        } finally {
          if (mountedRef.current) setWaitingAudio(current => ({ ...current, [saved.id]: false }));
        }
      })();
    } else {
      setWaitingAudio(current => ({ ...current, [draft.id]: false }));
      if (!richAudioAdded && failure === null && !aborted && saved?.status === 'done' && !saved.audioError && saved.content.trim() !== '' && deliveryEpoch === audioDeliveryEpoch.current) void voiceController.autoSpeakAssistant(saved.id, saved.content);
    }

    try {
      const count = await messageRepo.countMessages(conversation.id);
      const title =
        conversation.title === NEW_CONVERSATION_TITLE ? deriveTitle(text) : conversation.title;
      const updated = await conversationRepo.updateConversation(conversation.id, {
        messageCount: count,
        title,
      });
      if (updated !== undefined && mountedRef.current) setConversation(updated);
    } catch {
      // 会话元数据（标题 / 计数）更新失败不影响消息本身
    }

    // 自动记忆：默认跟随对话模型，也可在记忆页单独选择官方或自定义提供商。
    // 它是一次独立的模型调用，因此会增加 token 消耗；管理后台未开启时一律不执行。
    if (
      failure === null &&
      !aborted &&
      saved !== undefined &&
      saved.content.trim() !== '' &&
      getFeatureFlags().autoMemory
    ) {
      const target = resolveAutoMemoryAdapter(effectiveConversation, providers);
      if (target !== null) {
        void enqueueAutoMemory({
          adapter: target.adapter,
          model: target.model,
          conversationId: conversation.id,
          recent: history.slice(-4),
          userText: text,
          assistantText: saved.content,
        })
          .then((created) => {
            if (created.length > 0 && mountedRef.current) {
              setMemories((previous) => [...previous, ...created]);
            }
          })
          .catch(() => {
            // Automatic memory is best-effort and must never disturb the reply.
          });
      }
    }
  }, [
    attachments,
    conversation,
    input,
    memories,
    messages,
    providers,
    officialImageOptions,
    officialModelOptions,
    generateImage,
    saveImages,
    searchEnabled,
    searchRunner,
    selectedPreset,
    effectiveConversation,
    roleResolution.error,
    streaming,
    savingSelection,
    startImageMessage,
    voiceController,
    voice,
    ttsAccount,
    voiceSelectionKey,
    ttsMode,
    voiceDisplay.waitForAudio,
    voiceDisplay.voiceBubble,
  ]);

  const removeMessagePair = useCallback(async (userMessage: ChatMessage): Promise<ChatMessage[]> => {
    const index = messages.findIndex(message => message.id === userMessage.id);
    const next = index >= 0 ? messages[index + 1] : undefined;
    const ids = [userMessage.id, next?.role === 'assistant' ? next.id : null].filter((id): id is string => id !== null);
    if (next?.role === 'assistant' && (next.status === 'pending' || next.status === 'streaming' || (next.backgroundJob !== undefined && !next.backgroundJob.saved))) {
      if (next.imageJob !== undefined) abortImageJob(next.id);
      activeChatController(next.id)?.abort();
      if (next.backgroundJob !== undefined && !next.backgroundJob.saved) {
        void apiClient.post(`/v1/chat/jobs/${encodeURIComponent(next.backgroundJob.id)}/stop`, {}).catch(() => undefined);
      }
    }
    await Promise.all(ids.map(id => messageRepo.deleteMessage(id)));
    const remaining = messages.filter(message => !ids.includes(message.id));
    const updated = await conversationRepo.updateConversation(userMessage.conversationId, { messageCount: remaining.length });
    if (updated !== undefined && mountedRef.current) setConversation(updated);
    if (mountedRef.current) setMessages(remaining);
    return remaining;
  }, [messages]);

  const handleDeleteMessage = useCallback(async (message: ChatMessage): Promise<void> => {
    if (streaming) {
      setError('生成进行中，请先停止生成后再操作');
      return;
    }
    try {
      await messageRepo.deleteMessage(message.id);
      const remaining = await messageRepo.listMessages(message.conversationId);
      const updated = await conversationRepo.updateConversation(message.conversationId, { messageCount: remaining.length });
      if (mountedRef.current) {
        setMessages(remaining);
        if (updated !== undefined) setConversation(updated);
      }
      setSelectedUserMessageId(null);
    } catch {
      setError('删除消息失败，请重试');
    }
  }, [streaming]);

  const handleRegenerateUserMessage = useCallback(async (userMessage: ChatMessage): Promise<void> => {
    if (streaming) {
      setError('生成进行中，请先停止生成后再操作');
      return;
    }
    try {
      const remaining = await removeMessagePair(userMessage);
      setSelectedUserMessageId(null);
      void handleSend({ text: userMessage.content, attachments: userMessage.attachments ?? [], history: remaining });
    } catch {
      setError('重新生成失败，请重试');
    }
  }, [handleSend, removeMessagePair, streaming]);

  const handleStop = useCallback((): void => {
    if (abortRef.current) abortRef.current.abort();
    else {
      const job = messages.find(message => message.backgroundJob && !message.backgroundJob.saved)?.backgroundJob;
      if (job) void apiClient.post(`/v1/chat/jobs/${encodeURIComponent(job.id)}/stop`, {}).catch(() => { if (mountedRef.current) setError('停止失败，请联网后重试'); });
    }
  }, [messages]);

  const handleToggleSearch = useCallback((next: boolean): void => {
    setSearchEnabled(saveSearchEnabled(next));
  }, []);

  /** 选附件：复用 T09 的选择 + 校验结果；超限 / 类型非法逐个列出，不静默丢弃。 */
  const handlePickAttachments = useCallback((): void => {
    void picker({ existingCount: attachments.length })
      .then((result) => {
        if (!mountedRef.current) return;
        if (result.canceled) return;
        if (result.attachments.length > 0) {
          setAttachments((prev) => [...prev, ...result.attachments]);
        }
        if (result.rejected.length > 0) {
          const detail = result.rejected
            .map((item) => `${item.file.name}（${item.reason}）`)
            .join('；');
          setError(`有 ${result.rejected.length} 个文件未添加：${detail}`);
        } else {
          setError(null);
        }
      })
      .catch(() => {
        if (mountedRef.current) setError('选择文件失败，请重试');
      });
  }, [attachments.length, picker]);

  const handleRemoveAttachment = useCallback((id: string): void => {
    setAttachments((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleOpenGallery = useCallback((): void => {
    setGalleryNotice(null);
    if (typeof window === 'undefined') return;
    window.location.hash = roleGalleryHash(conversation?.presetId, conversation?.id);
  }, [conversation?.presetId, conversation?.id]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleSend();
    }
  };

  const openSession = async (session: Conversation): Promise<void> => {
    if (switchingRef.current) return;
    switchingRef.current = true;
    try {
      await messageRepo.markConversationRead(session.id);
      setActiveConversationId(session.id);
      setSessionsOpen(false);
      onConversationChange(session.id);
    } catch { setError('打开会话失败，请重试'); }
    finally { switchingRef.current = false; }
  };

  const createSession = async (): Promise<void> => {
    if (!conversation || switchingRef.current) return;
    switchingRef.current = true;
    setSavingSelection(true);
    try {
      const session = await conversationRepo.createAndSaveConversation({
        title: selectedPreset?.name ?? NEW_CONVERSATION_TITLE,
        settings: { ...conversation.settings },
        ...(conversation.presetId ? { presetId: conversation.presetId } : {}),
        ...(conversation.providerId !== undefined ? { providerId: conversation.providerId } : {}),
        ...(conversation.model !== undefined ? { model: conversation.model } : {}),
        ...(conversation.modelKind !== undefined ? { modelKind: conversation.modelKind } : {}),
      });
      setActiveConversationId(session.id);
      onConversationChange(session.id);
    } catch { setError('新建对话失败，请重试'); }
    finally { switchingRef.current = false; if (mountedRef.current) setSavingSelection(false); }
  };

  const canSend = conversation !== null && !savingSelection && (input.trim() !== '' || attachments.length > 0);
  const showProviderNotice =
    loaded &&
    conversation !== null &&
    providers.length === 0 &&
    officialModels.length === 0 &&
    officialImageOptions.length === 0;

  const page = (
    <section
      ref={rootRef}
      className={styles.page}
      style={{ '--chat-composer-height': `${composerHeight}px` } as CSSProperties}
      data-ui-id="page.chat.root"
      data-ui-role="container"
    >
      <div
        ref={headerRef}
        className={styles.header}
        style={boxStyle(chatLayout('chat.header'), 3)}
        data-ui-id="chat.header"
        data-ui-role="container"
        data-skin-surface="panel"
      >
        <motion.button
          ref={backRef}
          type="button"
          className={styles.backButton}
          style={boxStyle(chatLayout('chat.header.backButton'), 4)}
          data-ui-id="chat.header.backButton"
          aria-label="返回"
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={() => navigate('/messages')}
        >
          <AppIcon name="back" size={24} />
        </motion.button>
        <motion.button ref={newConversationRef} type="button" className={styles.headerButton}
          style={boxStyle(chatLayout('chat.header.newConversation'), 6)} data-ui-id="chat.header.newConversation"
          aria-label="开启新对话" title="开启新对话" disabled={conversation === null || savingSelection}
          whileTap={presets.tap.whileTap} transition={presets.tap.transition} onClick={() => { void createSession(); }}>
          <AppIcon name="new-chat" size={22} />
        </motion.button>
        <motion.button ref={sessionsRef} type="button" className={styles.headerButton}
          style={boxStyle(chatLayout('chat.header.sessions'), 6)} data-ui-id="chat.header.sessions"
          aria-label="打开会话列表" title="打开会话列表" aria-haspopup="dialog" aria-expanded={sessionsOpen}
          disabled={conversation === null || savingSelection} whileTap={presets.tap.whileTap} transition={presets.tap.transition}
          onClick={() => setSessionsOpen(true)}>
          <AppIcon name="session-list" size={22} />
        </motion.button>
        <motion.button
          ref={galleryRef}
          type="button"
          className={styles.headerButton}
          style={boxStyle(chatLayout('chat.header.galleryButton'), 6)}
          data-ui-id="chat.header.galleryButton"
          aria-label="图库"
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={handleOpenGallery}
        >
          <AppIcon name="gallery" size={22} />
        </motion.button>
        <SearchToggle
          enabled={searchEnabled}
          onToggle={handleToggleSearch}
          style={boxStyle(searchLayout('chat.search.toggleButton'), 6)}
        />
        <h1
          ref={titleRef}
          className={styles.title}
          style={boxStyle(chatLayout('chat.header.title'), 4)}
          data-ui-id="chat.header.title"
        >
          {conversation?.title ?? '对话'}
        </h1>

      </div>

      <ChatControlsDrawer hideModelSettings={selectedPreset !== null} selectionLabel={`${officialSelected ? '栀歆模型广场' : selectedProvider?.name ?? '选择提供商'} · ${currentModel || '请选择模型'}${!officialSelected && currentModelKind === 'image' && currentModel ? ' · 生图' : ''}`}>
        {selectedPreset ? <button ref={roleModelRef} type="button" className={`${styles.primaryButton} ${styles.roleModelButton}`} data-ui-id="chat.controls.roleModel" aria-label="角色模型配置"
          onClick={() => { window.location.hash = `/contacts?edit=${encodeURIComponent(selectedPreset.id)}&model=1&return=chat`; }}>
          <AppIcon name="settings" size={18} /> 角色模型配置
        </button> : null}
        {!selectedPreset && !showProviderNotice ? <>
        <select
          ref={providerSelectRef}
          className={styles.modelSelect}
          style={{ width: '100%', marginBottom: 12 }}
          data-ui-id="chat.header.providerSelect" data-ui-role="input"
          aria-label="提供商选择" value={selectedProviderId}
          disabled={conversation === null || streaming || savingSelection}
          onChange={event => {
            const id = event.target.value;
            // Provider and model are independent choices; keep the model at
            // "选择模型" until the user explicitly picks one.
            void saveModelSelection(id, '');
          }}
        >
          <option value="">选择提供商</option>
          <option value={SERVER_PROVIDER_ID}>栀歆模型</option>
          {providers.filter(provider => providerPurpose(provider) !== 'tts').map(provider => (
            <option key={provider.id} value={provider.id}>
              {`${provider.name}${providerPurpose(provider) === 'image' ? '（生图）' : '（语言）'}`}
            </option>
          ))}
          {selectedProviderId && !officialSelected && !selectedProvider ? <option value={selectedProviderId} disabled>提供商已停用</option> : null}
        </select>
        <select
          ref={modelSelectRef}
          className={styles.modelSelect}
          style={{ width: '100%', marginBottom: 12 }}
          data-ui-id="chat.header.modelSelect"
          data-ui-role="input"
          aria-label="模型选择"
          value={selectableModels.includes(currentModel) ? currentModel : ''}
          title={modelsError ?? (modelsLoading ? '正在加载模型列表' : currentModel)}
          aria-busy={modelsLoading}
          disabled={conversation === null || streaming || savingSelection || (modelsLoading && selectableModels.length === 0)}
          onChange={(event) => {
            const value = event.target.value;
            if (!value) return;
            void saveModelSelection(selectedProviderId, value);
          }}
        >
          <option value="">选择模型</option>
          {modelsLoading ? <option disabled>正在加载模型…</option> : null}
          {modelsError ? <option disabled>加载失败，请刷新</option> : null}
          {!modelsLoading && !modelsError && selectableModels.length === 0 ? <option disabled>暂无可用模型</option> : null}
          {selectableModels.map(model => <option key={model} value={model}>{model}</option>)}
        </select>
        <motion.button
          ref={refreshModelsRef} type="button" className={styles.headerButton}
          style={{ minHeight: 44, width: '100%' }}
          data-ui-id="chat.header.refreshModels" aria-label="刷新模型列表"
          title={modelsError ?? '刷新模型列表'}
          disabled={conversation === null || streaming || savingSelection || modelsLoading}
          whileTap={presets.tap.whileTap} transition={presets.tap.transition}
          onClick={() => { if (officialSelected) void refreshOfficialModels(); else catalog.refresh(); }}
        >↻ 刷新模型列表</motion.button>
        </> : null}
      <PresetSelector presetList={presetList} selectedId={conversation?.presetId ?? null}
        disabled={conversation === null || conversation.presetId != null || streaming || savingSelection} onSelect={(presetId) => { void handleSelectPreset(presetId); }} />
      <VoiceComposerControls />
      {!voice ? <div className={styles.ttsModes}>
        <TtsModeSelector value={ttsMode} onChange={(mode) => {
          audioDeliveryEpoch.current++; voiceController.clearPreparedAudio(); setWaitingAudio({}); setVoiceReplies({}); setTtsMode(mode);
        }} userId={ttsAccount?.userId} conversationId={conversation?.id} disabled={!ttsAccount} />
      </div> : null}
      </ChatControlsDrawer>
      <AnimatePresence>{sessionsOpen && conversation ? <RoleSessions conversation={conversation}
        title={selectedPreset?.name ?? '对话'} onClose={() => setSessionsOpen(false)} onOpen={session => { void openSession(session); }} /> : null}</AnimatePresence>
      <div
        ref={messagesRef}
        className={styles.messages}
        style={boxStyle({ ...chatLayout('chat.messages'), height: chatLayout('chat.messages').height - composerGrowth }, 3)}
        data-ui-id="chat.messages"
        data-ui-role="list"
      >
        <motion.ul
          className={styles.messageList}
          aria-label="消息列表"
          variants={presets.listStagger.container}
          initial="hidden"
          animate="visible"
        >
          {messages.map((message, index) => (
            <MessageBubble
              key={message.id}
              message={message}
              rolePreset={selectedPreset}
              index={index}
              presets={presets}
              citations={citations[message.id]}
              onPreview={setPreview}
              injection={injections[message.id]}
              showContextInjection={displayFeatures.showContextInjection}
              showReasoning={displayFeatures.showReasoning}
              ttsEnabled={voice !== undefined || (ttsMode !== 'off' && ttsAccount !== null)}
              waitingForAudio={voiceDisplay.waitForAudio && (voice !== undefined || (ttsMode !== 'off' && ttsAccount !== null)) && waitingAudio[message.id] === true}
              voiceReply={message.role === 'assistant' && message.status === 'done' && voiceDisplay.waitForAudio && voiceDisplay.voiceBubble && (voice !== undefined || (ttsMode !== 'off' && ttsAccount !== null)) ? voiceReplies[message.id] ?? readVoiceReply(ttsAccount?.userId ?? 'injected-voice', message.id, voiceSelectionKey) : undefined}
              actionsOpen={selectedUserMessageId === message.id}
              onToggleActions={message.role === 'user' ? () => setSelectedUserMessageId(current => current === message.id ? null : message.id) : undefined}
              onDeleteMessage={() => { void handleDeleteMessage(message); }}
              onRegenerateMessage={message.role === 'user' ? () => { void handleRegenerateUserMessage(message); } : undefined}
            />
          ))}
        </motion.ul>
      </div>

      {loaded && conversation === null ? <ChatEmptyState presets={presets} /> : null}
      {showProviderNotice ? <ProviderNotice presets={presets} /> : null}
      <AnimatePresence>{error !== null || roleResolution.error !== null ? <ErrorBanner message={error ?? roleResolution.error!} /> : null}</AnimatePresence>
      {cancelled ? <StatusHint message={CANCELLED_HINT} /> : null}
      {!cancelled && searchNotice !== null ? (
        <SearchNotice
          message={searchNotice}
          style={boxStyle(searchLayout('chat.search.notice'), 9)}
        />
      ) : null}

      <div
        ref={composerRef}
        className={styles.composer}
        style={boxStyle({ ...chatLayout('chat.composer.bar'), y: COMPOSER_TOP - composerGrowth, height: composerHeight }, 3)}
        data-ui-id="chat.composer.bar"
        data-ui-role="container"
        data-skin-surface="panel"
      >
        <motion.button
          ref={attachRef}
          type="button"
          className={styles.attachButton}
          style={boxStyle(chatLayout('chat.composer.attachButton'), 16, composerButtonOrigin)}
          data-ui-id="chat.composer.attachButton"
          aria-label="上传图片/文件"
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={handlePickAttachments}
        >
          <AppIcon name="attach" size={22} />
        </motion.button>
        <textarea
          ref={inputRef}
          className={styles.input}
          style={boxStyle({ ...chatLayout('chat.composer.input'), height: inputHeight }, 4, COMPOSER_CHILD_ORIGIN)}
          data-ui-id="chat.composer.input"
          data-ui-role="input"
          aria-label="消息输入框"
          placeholder="输入消息…"
          rows={1}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
        />
        <motion.button
          ref={sendRef}
          type="button"
          className={streaming ? styles.stopButton : styles.sendButton}
          style={boxStyle(chatLayout('chat.composer.sendButton'), 16, composerButtonOrigin)}
          data-ui-id="chat.composer.sendButton"
          aria-label={streaming ? '停止' : '发送'}
          disabled={!streaming && !canSend}
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={() => {
            if (streaming) handleStop();
            else void handleSend();
          }}
        >
          {streaming ? '停止' : '发送'}
        </motion.button>
      </div>

      {attachments.length > 0 ? (
        <div className={styles.stripLayer} style={{ transform: `translateY(${-composerGrowth}px)` }}>
          <AttachmentStrip
            attachments={attachments}
            layout={composerAttachmentsLayout()}
            page="chat"
            onRemove={handleRemoveAttachment}
          />
        </div>
      ) : null}

      <AnimatePresence>
        {galleryNotice !== null ? (
          <GalleryNotice message={galleryNotice} onOpen={handleOpenGallery} />
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {preview !== null ? (
          <MessageImagePreview
            attachment={preview}
            onClose={() => {
              setPreview(null);
            }}
          />
        ) : null}
      </AnimatePresence>

    </section>
  );

  return <VoiceProvider value={voiceContext}>{page}</VoiceProvider>;
}
